import { useEffect, useMemo, useState, FormEvent, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';
import { Lottery } from 'src/util/types';

type Channel = {
  id: string;
  name: string;
  lotteryId?: string;
  lottery?: Lottery | null;
};

export default function Channels() {
  const { tokens } = useAuth();
  const headers = useMemo(
    () => (tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : undefined),
    [tokens?.accessToken]
  );

  const sanitizeChannelName = useCallback((value: string) => {
    return value
      .trim()
      .replace(/\s+/g, '-');
  }, []);

  const [channels, setChannels] = useState<Channel[]>([]);
  const [name, setName] = useState('');
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeLotteries, setActiveLotteries] = useState<Lottery[]>([]);
  const [selectedLotteryId, setSelectedLotteryId] = useState<string>('');

  const fetchChannels = useCallback(async () => {
    if (!headers) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await axios.get(`${config.apiUrl}/api/channels/all`, { headers });
      const payload = res?.data?.content ?? res?.data ?? [];
      const arr = Array.isArray(payload) ? payload : [];
      
      setChannels(
        arr.map((item: any) => {
          const lotteryData = item?.lottery ?? item?.lotteryDto ?? null;
          return {
            id: item.id,
            name: item.name,
            lotteryId: item?.lotteryId ?? lotteryData?.id,
            lottery: lotteryData
          };
        })
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  useEffect(() => {
    const fetchActiveLotteries = async () => {
      if (!headers) return;
      try {
        const res = await axios.get(`${config.apiUrl}/api/lottery/current`, { headers });
        const payload = res?.data?.content ?? res?.data ?? [];
        const arr = Array.isArray(payload) ? payload : payload ? [payload] : [];
        setActiveLotteries(arr);
      } catch (err: any) {
        // ignore for now
      }
    };
    fetchActiveLotteries();
  }, [headers]);

  const resetForm = () => {
    setName('');
    setSelectedLotteryId('');
    setEditingChannel(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!headers) return;
    setError(null);
    setSuccess(null);

    const sanitizedName = sanitizeChannelName(name);

    if (!sanitizedName) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    try {
      if (editingChannel) {
        await axios.put(
          `${config.apiUrl}/api/channels/update/${encodeURIComponent(editingChannel.id)}`,
          { name: sanitizedName, lotteryId: selectedLotteryId || undefined },
          { headers }
        );
        setSuccess('Channel updated');
      } else {
        await axios.post(
          `${config.apiUrl}/api/channels/create`,
          { name: sanitizedName, lotteryId: selectedLotteryId || undefined },
          { headers }
        );
        setSuccess('Channel created');
      }
      await fetchChannels();
      resetForm();
      setCreateDialogOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (channel: Channel) => {
    setEditingChannel(channel);
    setName(sanitizeChannelName(channel.name));
    setSelectedLotteryId(channel.lotteryId ?? '');
    setCreateDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!headers || !deleteTarget) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.delete(`${config.apiUrl}/api/channels/delete/${encodeURIComponent(deleteTarget)}`, { headers });
      setSuccess('Channel deleted');
      await fetchChannels();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const cancelDelete = () => setDeleteTarget(null);

  const handleView = async (channel: Channel) => {
    if (!headers) return;
    setError(null);
    setSuccess(null);

    if (!channel.lotteryId) {
      setError('please select an active lottery ');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `${config.apiUrl}/api/lottery/${encodeURIComponent(channel.lotteryId)}`,
        { headers }
      );
      const lotteryData = res?.data?.content ?? res?.data;
      if (!lotteryData?.active) {
        setError('please select an active lottery ');
        return;
      }
      const url = `${window.location.origin}/channel/${encodeURIComponent(channel.name)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError('please select an active lottery ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Channels</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
          New Channel
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading && channels.length === 0 ? (
        <Typography>Loading...</Typography>
      ) : channels.length === 0 ? (
        <Typography>No channels found.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Lottery</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {channels.map((ch) => (
              <TableRow key={ch.id}>
                <TableCell>{ch.name}</TableCell>
                <TableCell>{ch.lottery?.name ?? ''}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" aria-label="view" onClick={() => handleView(ch)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="edit" onClick={() => startEdit(ch)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="delete" onClick={() => handleDelete(ch.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={createDialogOpen} onClose={() => { setCreateDialogOpen(false); resetForm(); }} fullWidth maxWidth="sm">
        <DialogTitle>{editingChannel ? 'Edit Channel' : 'New Channel'}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(sanitizeChannelName(e.target.value))}
                required
                fullWidth
              />
              <TextField
                select
                SelectProps={{ native: true }}
                label="Assign Lottery"
                value={selectedLotteryId}
                onChange={(e) => setSelectedLotteryId(e.target.value)}
                fullWidth
              >
                <option value="">None</option>
                {activeLotteries.map((lot) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.name}
                  </option>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setCreateDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button type="submit" disabled={loading} variant="contained">
              {editingChannel ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={cancelDelete}>
        <DialogTitle>Delete channel?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the selected channel. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button color="error" onClick={confirmDelete} disabled={loading}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
